// «Спящая» БД — значение контекста PowerSync, пока настоящей базы на вкладке
// нет (гость, краулер — см. `local-db-gate.ts`).
//
// Почему не `null`. SDK-хуки (@powersync/react 2.0) пустой контекст переживают
// плохо: `useStatus` читает `currentStatus` без проверки и роняет экран, а
// `useQuery` выходит раньше своих внутренних хуков. Второе страшнее, чем
// выглядит: когда база появляется у уже смонтированного компонента (вход без
// перезагрузки на Capacitor, первый вход на устройстве — база открывается после
// ответа о сессии), число хуков меняется, и React падает на «Rendered more
// hooks». Перемонтировать ради этого дерево роутера нельзя: под ним живут
// одноразовые шаги входа (возврат из IEC ID держит защиту от повтора в
// `useRef`), и повтор уводил бы нового пользователя не на тот адрес.
//
// Спящая база — непустой контекст с той же формой. SDK-хуки ведут себя с ней
// как с настоящей, а её смену на настоящую отрабатывают сами: их эффекты
// зависят от `powerSync` и пересоздают подписки. Ответы — правда для гостя:
// запрос пуст и не грузится, синк не подключён и первичного синка не было.
//
// Поверхность — ровно то, что трогают SDK-хуки (`customQuery`, `getAll`,
// `currentStatus`, `registerListener`, `syncStream`). Всё остальное у спящей
// базы не вызывается: императивный доступ идёт через `getPowerSync()` /
// `peekPowerSync()`, а не через контекст. Сверку с SDK держит тест рядом.

import type { CommonPowerSyncDatabase } from "@powersync/common";

// `data` не замораживаем: SDK отдаёт свои пустые массивы изменяемыми, и
// потребитель, сортирующий результат на месте, не должен падать у гостя.
const EMPTY_QUERY_STATE = Object.freeze({
	isLoading: false,
	isFetching: false,
	error: undefined,
	data: [] as unknown[],
});

interface DormantListener {
	settingsWillUpdate?: () => void;
}

/** Наблюдаемый запрос, который никогда ничего не найдёт. */
function dormantWatchedQuery() {
	return {
		state: EMPTY_QUERY_STATE,
		registerListener(listener: DormantListener) {
			// SDK считает запрос «перезапрашивается», пока не услышит
			// `settingsWillUpdate` после `updateSettings`. Не ответить — значит
			// навсегда оставить `isFetching: true` после смены параметров.
			if (listener.settingsWillUpdate) queueMicrotask(listener.settingsWillUpdate);
			return () => undefined;
		},
		updateSettings: () => Promise.resolve(),
		close: () => Promise.resolve(),
	};
}

const DORMANT_STATUS = Object.freeze({
	connected: false,
	connecting: false,
	downloading: false,
	uploading: false,
	downloadError: undefined,
	uploadError: undefined,
	lastSyncedAt: undefined,
	hasSynced: undefined,
	downloadProgress: null,
	dataFlowStatus: Object.freeze({ downloading: false, uploading: false }),
	priorityStatusEntries: Object.freeze([]),
	syncStreams: undefined,
	forStream: () => undefined,
});

const DORMANT = {
	currentStatus: DORMANT_STATUS,
	registerListener: () => () => undefined,
	customQuery: () => ({
		watch: dormantWatchedQuery,
		differentialWatch: dormantWatchedQuery,
	}),
	getAll: () => Promise.resolve([]),
	syncStream: () => ({ subscribe: () => new Promise<never>(() => undefined) }),
};

/**
 * Спящая база. Объект один на приложение: смена «спящая → настоящая» случается
 * не чаще раза за жизнь документа, и сравнение по ссылке это различает.
 */
export const DORMANT_POWERSYNC = DORMANT as unknown as CommonPowerSyncDatabase;

export function isDormantPowerSync(db: CommonPowerSyncDatabase): boolean {
	return db === DORMANT_POWERSYNC;
}
